import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  getEmployeeCountByDeliveryUnit,
  getEmployeeCountByDepartment,
  getNewHiredEmployees,
  getTotalEmployees,
  getTrailerCountByMakeModel,
  getTruckCountByMakeModel,
  getUpcomingBirthdays,
  getUpcomingWorkAnniversaries,
} from "../api/employeeService";
import {
  getUpcomingDriverBirthdays,
  getUpcomingDriverWorkAnniversaries,
} from "../api/driverService";
import useAuth from "../hooks/useAuth";
import { EmployeeDto } from "../interfaces/HR/employee";
import { DriverDto } from "../interfaces/Fleet/DriverDto";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  CircularProgress,
  Alert,
  Stack,
  Chip,
  Paper,
  Tooltip,
  IconButton,
} from "@mui/material";
import CakeIcon from "@mui/icons-material/Cake";
import WorkIcon from "@mui/icons-material/Work";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { StatCard } from "../components/General/StatCard/StatCard";
import { StatGroup } from "../components/General/StatGroup/StatGroup";
import PeopleIcon from "@mui/icons-material/People";
import DirectionsTruckIcon from "@mui/icons-material/LocalShipping";
import TrailerIcon from "@mui/icons-material/RvHookup";
import EventIcon from "@mui/icons-material/Event";
import { getExternalEvents } from "../api/externalEventService";
function formatDate(dateString: string) {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: undefined,
    month: "short",
    day: "numeric",
  });
}

function getDaysSince(dateString: string) {
  const date = new Date(dateString);
  const now = new Date();
  const diff = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
  );
  return diff;
}

function getDaysUntil(dateString: string) {
  const now = new Date();
  const date = new Date(dateString);
  let next = new Date(now.getFullYear(), date.getMonth(), date.getDate());
  if (now.getDate() === next.getDate() && now.getMonth() === next.getMonth()) {
    return 0;
  }
  if (next < now) {
    next = new Date(now.getFullYear() + 1, date.getMonth(), date.getDate());
  }
  const diff = Math.ceil(
    (next.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
  );
  return diff;
}

const getChipColor = (
  days: number,
  type: "newHire" | "birthday" | "anniversary",
) => {
  if (type === "newHire")
    return days <= 7 ? "success" : days <= 30 ? "info" : "default";
  if (days <= 3) return "warning";
  if (days <= 14) return "info";
  return "default";
};

const EmployeeCard = ({
  employee,
  icon,
  daysLabel,
  daysValue,
  dateLabel,
  dateValue,
  chipColor,
  secondaryChip,
}: {
  employee: EmployeeDto;
  icon: React.ReactNode;
  daysLabel: string;
  daysValue?: number;
  dateLabel: string;
  dateValue: string;
  chipColor: "success" | "info" | "warning" | "default";
  secondaryChip?: React.ReactNode;
}) => (
  <Card
    elevation={4}
    sx={{
      minWidth: { xs: 0, sm: 260 },
      maxWidth: { xs: "100%", sm: 340 },
      m: { xs: 0, sm: 1.5 },
      borderRadius: 4,
      transition: "transform 0.2s, box-shadow 0.2s",
      boxShadow: 3,
      "&:hover": { transform: "translateY(-6px)", boxShadow: 8 },
      background: (theme) => theme.palette.background.paper,
    }}
  >
    <CardContent>
      <Stack
        flexWrap={{ xs: "wrap", lg: "nowrap" }}
        useFlexGap
        direction="row"
        spacing={2}
        alignItems="center"
        mb={1}
      >
        <Avatar sx={{ bgcolor: "primary.main", width: 54, height: 54 }}>
          {icon}
        </Avatar>
        <Box>
          <Typography variant="h6" fontWeight="bold">
            {employee.firstName} {employee.lastName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {employee.position} | {employee.department}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {employee.email}
          </Typography>
        </Box>
      </Stack>
      <Stack
        flexWrap={{ xs: "wrap", lg: "nowrap" }}
        useFlexGap
        direction="row"
        spacing={1}
        alignItems="center"
        mt={2}
      >
        <Tooltip title={dateLabel + ": " + dateValue} arrow>
          <Chip
            label={
              typeof daysValue === "number" && daysValue !== 0
                ? `${daysValue} ${daysLabel}`
                : daysLabel
            }
            color={chipColor}
            variant="filled"
            sx={{ fontWeight: 600, fontSize: 15, px: 1.5 }}
          />
        </Tooltip>
        {secondaryChip}
      </Stack>
    </CardContent>
  </Card>
);

const DriverCard = ({
  driver,
  icon,
  daysLabel,
  daysValue,
  dateLabel,
  dateValue,
  chipColor,
  secondaryChip,
}: {
  driver: DriverDto;
  icon: React.ReactNode;
  daysLabel: string;
  daysValue?: number;
  dateLabel: string;
  dateValue: string;
  chipColor: "success" | "info" | "warning" | "default";
  secondaryChip?: React.ReactNode;
}) => {
  const driverSubtitle = [driver.company, driver.driverCode]
    .filter(Boolean)
    .join(" | ");
  return (
    <Card
      elevation={4}
      sx={{
        minWidth: { xs: 0, sm: 260 },
        maxWidth: { xs: "100%", sm: 340 },
        m: { xs: 0, sm: 1.5 },
        borderRadius: 4,
        transition: "transform 0.2s, box-shadow 0.2s",
        boxShadow: 3,
        "&:hover": { transform: "translateY(-6px)", boxShadow: 8 },
        background: (theme) => theme.palette.background.paper,
      }}
    >
      <CardContent>
        <Stack
          flexWrap={{ xs: "wrap", lg: "nowrap" }}
          useFlexGap
          direction="row"
          spacing={2}
          alignItems="center"
          mb={1}
        >
          <Avatar sx={{ bgcolor: "primary.main", width: 54, height: 54 }}>
            {icon}
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {driver.firstName} {driver.lastName}
            </Typography>
            {driverSubtitle && (
              <Typography variant="body2" color="text.secondary">
                {driverSubtitle}
              </Typography>
            )}
          </Box>
        </Stack>
        <Stack
          flexWrap={{ xs: "wrap", lg: "nowrap" }}
          useFlexGap
          direction="row"
          spacing={1}
          alignItems="center"
          mt={2}
        >
          <Tooltip title={dateLabel + ": " + dateValue} arrow>
            <Chip
              label={
                typeof daysValue === "number" && daysValue !== 0
                  ? `${daysValue} ${daysLabel}`
                  : daysLabel
              }
              color={chipColor}
              variant="filled"
              sx={{ fontWeight: 600, fontSize: 15, px: 1.5 }}
            />
          </Tooltip>
          {secondaryChip}
        </Stack>
      </CardContent>
    </Card>
  );
};

function getWorkAnniversaryYears(startDate: string) {
  const start = new Date(startDate);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const anniversaryThisYear = new Date(
    now.getFullYear(),
    start.getMonth(),
    start.getDate(),
  );

  const upcomingYear =
    anniversaryThisYear >= today ? now.getFullYear() : now.getFullYear() + 1;
  return upcomingYear - start.getFullYear();
}

const Section = ({
  title,
  icon,
  employees,
  loading,
  error,
  type,
  sectionColor,
}: {
  title: string;
  icon: React.ReactNode;
  employees: EmployeeDto[] | undefined;
  loading: boolean;
  error: any;
  type: "newHire" | "birthday" | "anniversary";
  sectionColor: string;
}) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const scrollAmount = 340;
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);
  const [showArrows, setShowArrows] = React.useState(false);

  const updateScrollState = React.useCallback(() => {
    const el = scrollRef.current;
    if (el) {
      setCanScrollLeft(el.scrollLeft > 0);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1); // -1 for rounding
      setShowArrows(el.scrollWidth > el.clientWidth + 1); // +1 for rounding
    }
  }, []);

  React.useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [employees, updateScrollState]);

  React.useEffect(() => {
    // Update on data change
    setTimeout(updateScrollState, 100);
  }, [employees, updateScrollState]);

  const handleScroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const { scrollLeft } = scrollRef.current;
      scrollRef.current.scrollTo({
        left:
          direction === "left"
            ? scrollLeft - scrollAmount
            : scrollLeft + scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <Paper
      elevation={6}
      sx={{
        mb: 5,
        borderRadius: 5,
        width: { xs: "100%", sm: "auto" },
        minWidth: 0,
        p: { xs: 1.5, sm: 3 },
        background: (theme) => theme.palette.background.default,
        borderLeft: `8px solid ${sectionColor}`,
        boxShadow: 4,
        maxWidth: "min(1200px, 100%)",
        mx: "auto",
      }}
    >
      <Box
        flexWrap={{ xs: "wrap", lg: "nowrap" }}
        display="flex"
        alignItems="center"
        mb={2}
        gap={1}
      >
        <Avatar sx={{ bgcolor: sectionColor, width: 40, height: 40 }}>
          {icon}
        </Avatar>
        <Typography
          variant="h5"
          fontWeight="bold"
          color={sectionColor}
          flex={1}
        >
          {title}
        </Typography>
        {showArrows && (
          <>
            <IconButton
              aria-label={`Previous ${title.toLowerCase()}`}
              onClick={() => handleScroll("left")}
              size="small"
              sx={{ mr: 1 }}
              disabled={!canScrollLeft}
            >
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
            <IconButton
              aria-label={`Next ${title.toLowerCase()}`}
              onClick={() => handleScroll("right")}
              size="small"
              disabled={!canScrollRight}
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </Box>
      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity="error">{error.message || error.toString()}</Alert>
      ) : employees && employees.length > 0 ? (
        <Box
          ref={scrollRef}
          sx={{
            flexWrap: { xs: "wrap", sm: "nowrap" },
            display: "flex",
            minWidth: 0,
            gap: { xs: 2, sm: 0 },
            overflowX: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
            pb: 1,
          }}
        >
          {employees.map((employee) => {
            let daysValue: number | undefined = 0;
            let daysLabel = "days";
            let dateLabel = "";
            let dateValue = "";
            let chipColor: "success" | "info" | "warning" | "default" =
              "default";
            let secondaryChip: React.ReactNode | undefined;
            if (type === "newHire") {
              daysValue = getDaysSince(employee.startDate);
              if (daysValue === 0) {
                daysLabel = "Today!";
                daysValue = undefined;
              } else if (daysValue < 0) {
                daysLabel = `In ${Math.abs(daysValue)} days`;
                daysValue = undefined;
              } else {
                daysLabel = daysValue === 1 ? "day ago" : "days ago";
              }
              dateLabel = "Hired on";
              dateValue = formatDate(employee.startDate);
              chipColor = getChipColor(daysValue ?? 0, type);
            } else if (type === "birthday") {
              daysValue = getDaysUntil(employee.birthDate);
              daysLabel =
                daysValue === 0
                  ? "Today!"
                  : daysValue === 1
                    ? "day left"
                    : "days left";
              dateLabel = "Birthday";
              dateValue = formatDate(employee.birthDate);
              chipColor = getChipColor(daysValue, type);
            } else if (type === "anniversary") {
              daysValue = getDaysUntil(employee.startDate);
              daysLabel =
                daysValue === 0
                  ? "Today!"
                  : daysValue === 1
                    ? "day left"
                    : "days left";
              dateLabel = "Anniversary";
              dateValue = formatDate(employee.startDate);
              chipColor = getChipColor(daysValue, type);
              const years = getWorkAnniversaryYears(employee.startDate);
              secondaryChip = (
                <Tooltip
                  title={`${years} ${years === 1 ? "year" : "years"} with C1`}
                  arrow
                >
                  <Chip
                    label={`${years} ${years === 1 ? "year" : "years"}`}
                    color="secondary"
                    sx={{ fontWeight: 600, fontSize: 15, px: 1.5 }}
                  />
                </Tooltip>
              );
            }
            return (
              <Box
                key={employee.id}
                sx={{
                  flex: { xs: "0 0 100%", sm: "0 0 auto" },
                  minWidth: 0,
                  maxWidth: "100%",
                }}
              >
                <EmployeeCard
                  employee={employee}
                  icon={icon}
                  daysLabel={daysLabel}
                  daysValue={daysValue}
                  dateLabel={dateLabel}
                  dateValue={dateValue}
                  chipColor={chipColor}
                  secondaryChip={secondaryChip}
                />
              </Box>
            );
          })}
        </Box>
      ) : (
        <Typography color="text.secondary">No data found.</Typography>
      )}
    </Paper>
  );
};

const DriverSection = ({
  title,
  icon,
  drivers,
  loading,
  error,
  type,
  sectionColor,
}: {
  title: string;
  icon: React.ReactNode;
  drivers: DriverDto[] | undefined;
  loading: boolean;
  error: any;
  type: "birthday" | "anniversary";
  sectionColor: string;
}) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const scrollAmount = 340;
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);
  const [showArrows, setShowArrows] = React.useState(false);

  const updateScrollState = React.useCallback(() => {
    const el = scrollRef.current;
    if (el) {
      setCanScrollLeft(el.scrollLeft > 0);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
      setShowArrows(el.scrollWidth > el.clientWidth + 1);
    }
  }, []);

  React.useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [drivers, updateScrollState]);

  React.useEffect(() => {
    setTimeout(updateScrollState, 100);
  }, [drivers, updateScrollState]);

  const handleScroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const { scrollLeft } = scrollRef.current;
      scrollRef.current.scrollTo({
        left:
          direction === "left"
            ? scrollLeft - scrollAmount
            : scrollLeft + scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <Paper
      elevation={6}
      sx={{
        mb: 5,
        borderRadius: 5,
        width: { xs: "100%", sm: "auto" },
        minWidth: 0,
        p: { xs: 1.5, sm: 3 },
        background: (theme) => theme.palette.background.default,
        borderLeft: `8px solid ${sectionColor}`,
        boxShadow: 4,
        maxWidth: "min(1200px, 100%)",
        mx: "auto",
      }}
    >
      <Box
        flexWrap={{ xs: "wrap", lg: "nowrap" }}
        display="flex"
        alignItems="center"
        mb={2}
        gap={1}
      >
        <Avatar sx={{ bgcolor: sectionColor, width: 40, height: 40 }}>
          {icon}
        </Avatar>
        <Typography
          variant="h5"
          fontWeight="bold"
          color={sectionColor}
          flex={1}
        >
          {title}
        </Typography>
        {showArrows && (
          <>
            <IconButton
              aria-label={`Previous ${title.toLowerCase()}`}
              onClick={() => handleScroll("left")}
              size="small"
              sx={{ mr: 1 }}
              disabled={!canScrollLeft}
            >
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
            <IconButton
              aria-label={`Next ${title.toLowerCase()}`}
              onClick={() => handleScroll("right")}
              size="small"
              disabled={!canScrollRight}
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </Box>
      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity="error">{error.message || error.toString()}</Alert>
      ) : drivers && drivers.length > 0 ? (
        <Box
          ref={scrollRef}
          sx={{
            flexWrap: { xs: "wrap", sm: "nowrap" },
            display: "flex",
            minWidth: 0,
            gap: { xs: 2, sm: 0 },
            overflowX: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
            pb: 1,
          }}
        >
          {drivers.map((driver) => {
            let daysValue: number | undefined = 0;
            let daysLabel = "days";
            let dateLabel = "";
            let dateValue = "";
            let chipColor: "success" | "info" | "warning" | "default" =
              "default";
            let secondaryChip: React.ReactNode | undefined;
            if (type === "birthday" && driver.birthDate) {
              daysValue = getDaysUntil(driver.birthDate);
              daysLabel =
                daysValue === 0
                  ? "Today!"
                  : daysValue === 1
                    ? "day left"
                    : "days left";
              dateLabel = "Birthday";
              dateValue = formatDate(driver.birthDate);
              chipColor = getChipColor(daysValue, type);
            } else if (type === "anniversary" && driver.workStartDate) {
              daysValue = getDaysUntil(driver.workStartDate);
              daysLabel =
                daysValue === 0
                  ? "Today!"
                  : daysValue === 1
                    ? "day left"
                    : "days left";
              dateLabel = "Anniversary";
              dateValue = formatDate(driver.workStartDate);
              chipColor = getChipColor(daysValue, type);
              const years = getWorkAnniversaryYears(driver.workStartDate);
              secondaryChip = (
                <Tooltip
                  title={`${years} ${years === 1 ? "year" : "years"} with C1`}
                  arrow
                >
                  <Chip
                    label={`${years} ${years === 1 ? "year" : "years"}`}
                    color="secondary"
                    sx={{ fontWeight: 600, fontSize: 15, px: 1.5 }}
                  />
                </Tooltip>
              );
            } else {
              daysLabel = "Date not set";
              daysValue = undefined;
              dateLabel = type === "birthday" ? "Birthday" : "Anniversary";
              dateValue = "Not set";
            }
            return (
              <Box
                key={driver.id}
                sx={{
                  flex: { xs: "0 0 100%", sm: "0 0 auto" },
                  minWidth: 0,
                  maxWidth: "100%",
                }}
              >
                <DriverCard
                  driver={driver}
                  icon={icon}
                  daysLabel={daysLabel}
                  daysValue={daysValue}
                  dateLabel={dateLabel}
                  dateValue={dateValue}
                  chipColor={chipColor}
                  secondaryChip={secondaryChip}
                />
              </Box>
            );
          })}
        </Box>
      ) : (
        <Typography color="text.secondary">No data found.</Typography>
      )}
    </Paper>
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();

  const {
    data: newHires,
    isLoading: loadingNewHires,
    error: errorNewHires,
  } = useQuery({
    queryKey: ["newHiredEmployees"],
    queryFn: () => {
      if (!token) throw new Error("No token");
      return getNewHiredEmployees(token);
    },
    enabled: !!token,
  });

  const {
    data: birthdays,
    isLoading: loadingBirthdays,
    error: errorBirthdays,
  } = useQuery({
    queryKey: ["upcomingBirthdays"],
    queryFn: () => {
      if (!token) throw new Error("No token");
      return getUpcomingBirthdays(token);
    },
    enabled: !!token,
  });

  const {
    data: anniversaries,
    isLoading: loadingAnniversaries,
    error: errorAnniversaries,
  } = useQuery({
    queryKey: ["upcomingWorkAnniversaries"],
    queryFn: () => {
      if (!token) throw new Error("No token");
      return getUpcomingWorkAnniversaries(token);
    },
    enabled: !!token,
  });

  const {
    data: driverBirthdays,
    isLoading: loadingDriverBirthdays,
    error: errorDriverBirthdays,
  } = useQuery({
    queryKey: ["upcomingDriverBirthdays"],
    queryFn: () => {
      if (!token) throw new Error("No token");
      return getUpcomingDriverBirthdays(token);
    },
    enabled: !!token,
  });

  const {
    data: driverAnniversaries,
    isLoading: loadingDriverAnniversaries,
    error: errorDriverAnniversaries,
  } = useQuery({
    queryKey: ["upcomingDriverWorkAnniversaries"],
    queryFn: () => {
      if (!token) throw new Error("No token");
      return getUpcomingDriverWorkAnniversaries(token);
    },
    enabled: !!token,
  });

  const { data: totalEmployees } = useQuery({
    queryKey: ["totalEmployees"],
    queryFn: () => {
      if (!token) throw new Error("No token");
      return getTotalEmployees(token);
    },
    enabled: !!token,
  });
  const { data: trucksByMakeModel } = useQuery({
    queryKey: ["trucksByMakeModel"],
    queryFn: () => {
      if (!token) throw new Error("No token");
      return getTruckCountByMakeModel(token);
    },
    enabled: !!token,
  });
  const { data: trailersByMakeModel } = useQuery({
    queryKey: ["trailersByMakeModel"],
    queryFn: () => {
      if (!token) throw new Error("No token");
      return getTrailerCountByMakeModel(token);
    },
    enabled: !!token,
  });
  const { data: employeesByDept } = useQuery({
    queryKey: ["employeesByDept"],
    queryFn: () => {
      if (!token) throw new Error("No token");
      return getEmployeeCountByDepartment(token);
    },
    enabled: !!token,
  });
  const { data: employeesByDU } = useQuery({
    queryKey: ["employeesByDU"],
    queryFn: () => {
      if (!token) throw new Error("No token");
      return getEmployeeCountByDeliveryUnit(token);
    },
    enabled: !!token,
  });
  const { data: externalEvents } = useQuery({
    queryKey: ["externalEventsHome"],
    queryFn: () => {
      if (!token) throw new Error("No token");
      return getExternalEvents(token);
    },
    enabled: !!token,
  });

  const upcomingExternalEvents =
    externalEvents
      ?.filter((event) => {
        const endDate =
          event.eventEndDate ?? event.eventStartDate ?? event.eventDate;
        return endDate ? new Date(endDate) >= new Date() : false;
      })
      .sort(
        (a, b) =>
          new Date(a.eventStartDate ?? a.eventDate ?? "").getTime() -
          new Date(b.eventStartDate ?? b.eventDate ?? "").getTime(),
      )
      .slice(0, 5) ?? [];

  return (
    <Box
      minHeight="100vh"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      sx={{ background: (theme) => theme.palette.grey[100] }}
    >
      {/* Main Stats Heading */}
      <Typography variant="h4" fontWeight="bold" align="center" sx={{ mb: 3 }}>
        C1 in Numbers
      </Typography>
      {/* Summary Statistics */}
      <Paper
        elevation={4}
        sx={{
          mb: 5,
          p: 3,
          borderRadius: 4,
          background: (theme) => theme.palette.background.paper,
          maxWidth: "min(1200px, 100%)",
          mx: "auto",
        }}
      >
        <Box display="flex" flexWrap="wrap" gap={2} justifyContent="center">
          <StatCard
            title="Total Employees"
            value={typeof totalEmployees === "number" ? totalEmployees : "..."}
            icon={<PeopleIcon />}
            color="#1976d2"
          />
          <StatCard
            title="Total Trucks"
            value={
              trucksByMakeModel
                ? Object.values(trucksByMakeModel).reduce((a, b) => a + b, 0)
                : "..."
            }
            icon={<DirectionsTruckIcon />}
            color="#43a047"
          />
          <StatCard
            title="Total Trailers"
            value={
              trailersByMakeModel
                ? Object.values(trailersByMakeModel).reduce((a, b) => a + b, 0)
                : "..."
            }
            icon={<TrailerIcon />}
            color="#ff9800"
          />
        </Box>
      </Paper>
      {/* End Summary Statistics */}

      {/* Drill-down statistics */}
      <Paper
        elevation={4}
        sx={{
          mb: 5,
          p: 3,
          borderRadius: 4,
          background: (theme) => theme.palette.background.paper,
          maxWidth: "min(1200px, 100%)",
          mx: "auto",
        }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          flexWrap="wrap"
          justifyContent="space-around"
          alignItems="center"
        >
          <StatGroup
            title="Employees by Department"
            data={employeesByDept}
            color="#1976d2"
          />
          <StatGroup
            title="Employees by Delivery Unit"
            data={employeesByDU}
            color="#43a047"
          />
        </Box>
      </Paper>
      {/* End drill-down statistics */}
      <Paper
        elevation={4}
        sx={{
          mb: 5,
          p: 3,
          borderRadius: 4,
          background: (theme) => theme.palette.background.paper,
          maxWidth: "min(1200px, 100%)",
          mx: "auto",
          width: "100%",
        }}
      >
        <Box
          flexWrap={{ xs: "wrap", lg: "nowrap" }}
          display="flex"
          alignItems="center"
          gap={1}
          mb={2}
        >
          <Avatar sx={{ bgcolor: "#6a1b9a" }}>
            <EventIcon />
          </Avatar>
          <Typography variant="h6" fontWeight="bold">
            Upcoming External Events
          </Typography>
        </Box>
        <Stack spacing={1.2}>
          {upcomingExternalEvents.length === 0 ? (
            <Typography color="text.secondary">
              No upcoming external events.
            </Typography>
          ) : (
            upcomingExternalEvents.map((event) => (
              <Paper
                key={event.id}
                variant="outlined"
                sx={{ p: 1.5, cursor: "pointer" }}
                onClick={() =>
                  navigate(
                    `/hr/external-events?date=${encodeURIComponent(
                      event.eventDate.split("T")[0],
                    )}`,
                  )
                }
              >
                <Typography variant="subtitle2" fontWeight={700}>
                  {event.eventName} •{" "}
                  {formatDate(
                    event.eventStartDate ??
                      event.eventEndDate ??
                      event.eventDate ??
                      new Date().toISOString(),
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {event.location} • {event.suitableDepartment}
                </Typography>
                <Chip
                  label={`${event.interestedCount} interested`}
                  size="small"
                  sx={{ mt: 1 }}
                />
                <Chip
                  label={`${event.goingCount} going`}
                  size="small"
                  sx={{ mt: 1, ml: 1 }}
                />
              </Paper>
            ))
          )}
        </Stack>
      </Paper>

      {/* Employee Events Heading */}
      <Typography variant="h4" fontWeight="bold" align="center" sx={{ mb: 3 }}>
        Employee Events
      </Typography>
      <Section
        title="New Hires"
        icon={<PersonAddIcon />}
        employees={newHires}
        loading={loadingNewHires}
        error={errorNewHires}
        type="newHire"
        sectionColor="#43a047"
      />
      <Section
        title="Upcoming Birthdays"
        icon={<CakeIcon />}
        employees={birthdays}
        loading={loadingBirthdays}
        error={errorBirthdays}
        type="birthday"
        sectionColor="#1976d2"
      />
      <Section
        title="Upcoming Work Anniversaries"
        icon={<WorkIcon />}
        employees={anniversaries}
        loading={loadingAnniversaries}
        error={errorAnniversaries}
        type="anniversary"
        sectionColor="#ff9800"
      />
      <Typography variant="h4" fontWeight="bold" align="center" sx={{ mb: 3 }}>
        Driver Events
      </Typography>
      <DriverSection
        title="Upcoming Driver Birthdays"
        icon={<CakeIcon />}
        drivers={driverBirthdays}
        loading={loadingDriverBirthdays}
        error={errorDriverBirthdays}
        type="birthday"
        sectionColor="#1976d2"
      />
      <DriverSection
        title="Upcoming Driver Work Anniversaries"
        icon={<WorkIcon />}
        drivers={driverAnniversaries}
        loading={loadingDriverAnniversaries}
        error={errorDriverAnniversaries}
        type="anniversary"
        sectionColor="#ff9800"
      />
    </Box>
  );
};

export default HomePage;
